import React, { useState, useEffect } from "react";
import { Table, Input, Space, Button, Spin, message, Modal, Select, Tag } from "antd";
import { SearchOutlined, BankOutlined } from "@ant-design/icons";
import axios from "axios";
import { BASE_URL } from "../constants/Constants";
import ReturnRequestDetails from "../components/ReturnRequestDetails";

const { Option } = Select;

const ReturnRequests = () => {
  const userId = localStorage.getItem("userId");
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  const [isBankModalOpen, setIsBankModalOpen] = useState(false);
  const [selectedBankDetails, setSelectedBankDetails] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const res = await axios.get(`${BASE_URL}/api/return-requests/all`);
        
        const returnData = res.data.map((item) => ({
          ...item,
          // FIX 1: Use 'orderNo' instead of 'serialNo' to get the correct Order ID
          orderId: item.order ? item.order.orderNo : "N/A",
          
          userId: item.order ? item.order.userId : "N/A",
          productName: item.order ? item.order.productName : "Unknown",
          
          // FIX 2: Use 'finalOrderAmount' (matches getFinalOrderAmount() in Java)
          orderedAmount: item.order ? item.order.finalOrderAmount : 0, 
          
          bankInfo: {
              accountHolderName: item.accountHolderName,
              accountNumber: item.accountNumber,
              bankName: item.bankName,
              ifscCode: item.ifscCode
          }
        }));
        setData(returnData);
      } catch (error) {
        console.error("Error fetching requests", error);
        message.error("Failed to fetch return requests");
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // --- SEARCH HANDLERS ---
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex, columnTitle) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div className="p-4">
        <Input
          placeholder={`Search ${columnTitle}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          className="mb-2"
        />
        <Space>
          <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>Reset</Button>
          <Button type="primary" onClick={() => handleSearch(selectedKeys, confirm, dataIndex)} size="small" style={{ width: 90 }}>OK</Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
    onFilter: (value, record) => record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    render: (text) => searchedColumn === dataIndex ? <strong>{text}</strong> : text,
  });

  // --- STATUS CHANGE HANDLER ---
  const handleStatusChange = async (id, newStatus) => {
    try {
        await axios.put(`${BASE_URL}/api/return-requests/update-status/${id}?status=${newStatus}`);
        message.success("Status updated successfully!");
        const updatedData = data.map(item => 
            item.id === id ? { ...item, status: newStatus } : item
        );
        setData(updatedData);
    } catch (error) {
        console.error("Update failed", error);
        message.error("Failed to update status");
    }
  };

  const showBankDetails = (bankInfo) => {
      setSelectedBankDetails(bankInfo);
      setIsBankModalOpen(true);
  };

  // --- COLUMNS ---
  const columns = [
    {
      title: "SR NO",
      key: "index",
      render: (text, record, index) => index + 1,
      className: "bg-boxdark p-2.5 text-black text-sm font-medium",
    },
    {
      title: "ORDER ID",
      dataIndex: "orderId",
      ...getColumnSearchProps("orderId", "Order ID"),
      className: "bg-boxdark text-black text-center p-2.5",
    },
    {
      title: "PRODUCT",
      dataIndex: "productName",
      className: "bg-boxdark text-black p-2.5",
      render: (text) => <span className="font-semibold">{text}</span>
    },
    // 2. NEW COLUMN FOR AMOUNT
    {
        title: "REFUND AMT",
        dataIndex: "orderedAmount",
        className: "bg-boxdark text-black p-2.5 font-bold",
        render: (amount) => <span className="text-green-600">₹ {amount}</span>
    },
    {
      title: "REASON",
      dataIndex: "returnReason",
      className: "bg-boxdark text-black p-2.5",
      render: (text, record) => (
          <div>
              <Tag color="volcano">{text}</Tag>
              <div className="text-xs text-gray-500 mt-1">{record.comment}</div>
          </div>
      )
    },
    {
      title: "BANK INFO",
      key: "bankInfo",
      className: "bg-boxdark text-center p-2.5",
      render: (_, record) => (
          <Button 
            icon={<BankOutlined />} 
            size="small" 
            onClick={() => showBankDetails(record.bankInfo)}
          >
              View
          </Button>
      )
    },
    {
      title: "CURRENT STATUS",
      dataIndex: "status",
      className: "bg-boxdark text-center p-2.5",
      render: (status, record) => (
        <Select
            defaultValue={status}
            style={{ width: 200 }}
            onChange={(value) => handleStatusChange(record.id, value)}
            status={status === "REFUNDED_SUCCESSFULLY" ? "" : "warning"}
        >
            <Option value="RETURN_REQUEST_INITIATED">Return Requested</Option>
            <Option value="PRODUCT_RETURNED_SUCCESSFULLY">Product Returned</Option>
            <Option value="REFUND_INITIATED">Refund Initiated</Option>
            <Option value="REFUNDED_SUCCESSFULLY">Refunded</Option>
        </Select>
      ),
    },
    {
      title: "DETAILS",
      key: "viewMore",
      render: (_, record) => <ReturnRequestDetails data={record} />, 
      className: "bg-boxdark p-2.5 text-center",
    },
  ];

  return userId ? (
    <div className="rounded-sm border border-stroke px-5 pt-6 pb-2.5 text-black shadow-default dark:border-strokedark dark:bg-boxdark dark:text-black sm:px-7.5 xl:pb-1">
      {isLoading ? (
        <div className="text-center p-10"><Spin size="large" /></div>
      ) : (
        <Table
          rowKey="id"
          className="text-black dark:text-black"
          columns={columns}
          dataSource={data}
          scroll={{ x: true }}
        />
      )}

      {/* BANK DETAILS MODAL */}
      <Modal
        title={<span><BankOutlined className="mr-2"/> Bank Details for Refund</span>}
        visible={isBankModalOpen}
        onCancel={() => setIsBankModalOpen(false)}
        footer={[
            <Button key="close" onClick={() => setIsBankModalOpen(false)}>Close</Button>
        ]}
      >
        {selectedBankDetails && (
            <div className="space-y-4">
                <div className="p-3 bg-gray-50 rounded border">
                    <p className="text-xs text-gray-500 uppercase">Account Holder</p>
                    <p className="font-bold text-lg">{selectedBankDetails.accountHolderName}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-gray-50 rounded border">
                        <p className="text-xs text-gray-500 uppercase">Bank Name</p>
                        <p className="font-semibold">{selectedBankDetails.bankName}</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded border">
                        <p className="text-xs text-gray-500 uppercase">IFSC Code</p>
                        <p className="font-mono">{selectedBankDetails.ifscCode}</p>
                    </div>
                </div>
                <div className="p-3 bg-gray-50 rounded border">
                    <p className="text-xs text-gray-500 uppercase">Account Number</p>
                    <p className="font-mono text-lg tracking-wider">{selectedBankDetails.accountNumber}</p>
                </div>
            </div>
        )}
      </Modal>
    </div>
  ) : (
    <div className="text-center p-10 bg-white rounded shadow">
        <h3>Please Sign in</h3>
    </div>
  );
};

export default ReturnRequests;